import 'styles/_reset.scss';
import { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ROUTE_PATH } from 'constants/routePath';
import Login from 'pages/common/Login';
import Student from 'pages/professor/Student';
import ClinicalPractice from 'pages/professor/ClinicalPractice';
import ScoreManagement from 'pages/professor/ScoreManagement';
import RegisteredRound from 'pages/professor/RegisteredRound';
import Statistics from 'pages/professor/Statistics';
import Notice from 'pages/common/Notice';
import NoticeDetail from 'pages/common/NoticeDetail';
import NoticeRegister from 'pages/common/NoticeRegister';
import Mypage from 'pages/mypage/Mypage';
import FindPassword from 'pages/common/FindPassword';
import ResetPassword from 'pages/common/ResetPassword';
import ChangePassword from 'pages/mypage/ChangePassword';
import Terms from 'pages/common/Terms';
import PrivacyPolicy from 'pages/common/PrivacyPolicy';
import TermsOfUse from 'pages/common/TermsOfUse';
import StudentDetail from 'pages/professor/StudentDetail';
import PracticeChart from 'pages/student/PracticeChart';
import EvaluationChart from 'pages/student/EvaluationChart';
import ScoreReport from 'pages/student/ScoreReport';
import RegisterPatient from 'pages/student/RegisterPatient';
import PatientManagement from 'pages/student/PatientManagement';
import PatientDetail from 'pages/student/PatientDetail';
import EditPatient from 'pages/student/EditPatient';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';
import Forbidden from 'pages/common/Forbidden';
import useModal from 'hooks/useModal';
import RequiredGuest from 'components/common/overlays/RequiredGuest';
import RequiredAuth from 'components/common/overlays/RequiredAuth';
import Modal from 'components/common/overlays/Modal';
import { UserRoleType } from 'types/apis/auth';
import NotFound from 'pages/common/NotFound';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ExtraIntraOralExaminationChartFeedback from 'pages/chart/professor/ExtraIntraOralExaminationChartFeedback';
import MedicalHistoryChartFeedback from 'pages/chart/professor/MedicalHistoryChartFeedback';
import DentalHistoryChartFeedback from 'pages/chart/professor/DentalHistoryChartFeedback';
import MethodsOfOralHygieneChartFeedback from 'pages/chart/professor/MethodsOfOralHygieneChartFeedback';
import VitalSignChartFeedback from 'pages/chart/professor/VitalSignChartFeedback';
import OralExaminationChartFeedback from 'pages/chart/professor/OralExaminationChartFeedback';
import OralMicrobialExaminationChartFeedback from 'pages/chart/professor/OralMicrobialExaminationChartFeedback';
import PreventiveDentalTreatmentChartFeedback from 'pages/chart/professor/PreventiveDentalTreatmentChartFeedback';
import PlaqueControlRecordChartFeedback from 'pages/chart/professor/PlaqueControlRecordChartFeedback';
import DHDiagnosisPlanEvaluationChartFeedback from 'pages/chart/professor/DHDiagnosisPlanEvaluationChartFeedback';
import PeriodontalExaminationChartFeedback from 'pages/chart/professor/PeriodontalExaminationChartFeedback';
import IntraOralCameraChartFeedback from 'pages/chart/professor/IntraOralCameraChartFeedback';
import DslrChartFeedback from 'pages/chart/professor/DslrChartFeedback';
import StudyModelChartFeedback from 'pages/chart/professor/StudyModelChartFeedback';
import DHImplementationChartFeedback from 'pages/chart/professor/DHImplementationChartFeedback';
import OralHealthEducationDataFeedback from 'pages/chart/professor/OralHealthEducationDataFeedback';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Tooltip,
  ChartDataLabels
);

const chartComponents = {
  MEDICAL_HISTORY_CHART: MedicalHistoryChartFeedback,
  DENTAL_HISTORY_CHART: DentalHistoryChartFeedback,
  METHODS_OF_ORAL_HYGIENE: MethodsOfOralHygieneChartFeedback,
  VITAL_SIGN_CHART: VitalSignChartFeedback,
  EXTRA_INTRA_ORAL_EXAMINATION_CHART: ExtraIntraOralExaminationChartFeedback,
  ORAL_EXAMINATION_CHART: OralExaminationChartFeedback,
  ORAL_MICROBIAL_EXAMINATION_CHART: OralMicrobialExaminationChartFeedback,
  PREVENTIVE_DENTAL_TREATMENT_CHART: PreventiveDentalTreatmentChartFeedback,
  PERIODONTAL_EXAMINATION_CHART: PeriodontalExaminationChartFeedback,
  DSLR_CHART: DslrChartFeedback,
  INTRA_ORAL_CAMERA_CHART: IntraOralCameraChartFeedback,
  PLAQUE_CONTROL_RECORD_CHART: PlaqueControlRecordChartFeedback,
  STUDY_MODEL_CHART: StudyModelChartFeedback,
  DH_IMPLEMENTATION_CHART: DHImplementationChartFeedback,
  DH_DIAGNOSIS_PLAN_EVALUTAION_CHART: DHDiagnosisPlanEvaluationChartFeedback,
  ORAL_HEALTH_EDUCATION_DATA_CHART: OralHealthEducationDataFeedback,
};

const App = () => {
  const { pathname } = useLocation();
  const { modal } = useModal();
  const userInfo = useAtomValue(userInfoAtom);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="App">
      <Routes>
        <Route
          path={ROUTE_PATH.common.LOGIN}
          element={
            <RequiredGuest>
              <Login />
            </RequiredGuest>
          }
        />
        <Route
          path={ROUTE_PATH.common.HOME}
          element={
            <Navigate
              to={
                userInfo
                  ? userInfo?.userRole === UserRoleType.STUDENT
                    ? ROUTE_PATH.student.PATIENT_MANAGEMENT
                    : ROUTE_PATH.professor.STUDENT
                  : ROUTE_PATH.common.LOGIN
              }
            />
          }
        />
        <Route
          path={ROUTE_PATH.common.FIND_PASSWORD}
          element={<FindPassword />}
        />
        <Route
          path={ROUTE_PATH.common.RESET_PASSWORD}
          element={<ResetPassword />}
        />
        <Route
          path={ROUTE_PATH.common.TERMS}
          element={
            <RequiredAuth>
              <Terms />
            </RequiredAuth>
          }
        />
        <Route
          path={ROUTE_PATH.common.PRIVACY_POLICY}
          element={
            <RequiredAuth>
              <PrivacyPolicy />
            </RequiredAuth>
          }
        />
        <Route
          path={ROUTE_PATH.common.TERMS_OF_USE}
          element={
            <RequiredAuth>
              <TermsOfUse />
            </RequiredAuth>
          }
        />
        {userInfo?.userRole === UserRoleType.PROFESSOR ? (
          <>
            <Route
              path={ROUTE_PATH.professor.STUDENT}
              element={
                <RequiredAuth>
                  <Student />
                </RequiredAuth>
              }
            />
            <Route
              path={`${ROUTE_PATH.professor.STUDENT}/:id`}
              element={
                <RequiredAuth>
                  <StudentDetail />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.professor.CLINICAL_PRACTICE}
              element={
                <RequiredAuth>
                  <ClinicalPractice />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.professor.SCORE_MANAGEMENT}
              element={
                <RequiredAuth>
                  <ScoreManagement />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.professor.REGISTERED_ROUND}
              element={
                <RequiredAuth>
                  <RegisteredRound />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.professor.STATISTICS}
              element={
                <RequiredAuth>
                  <Statistics />
                </RequiredAuth>
              }
            />
          </>
        ) : (
          <>
            <Route
              path={ROUTE_PATH.student.PATIENT_REGISTER}
              element={
                <RequiredAuth>
                  <RegisterPatient />
                </RequiredAuth>
              }
            />
            <Route
              path={`${ROUTE_PATH.student.PATIENT_REGISTER}/:id`}
              element={
                <RequiredAuth>
                  <EditPatient />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.student.PATIENT_MANAGEMENT}
              element={
                <RequiredAuth>
                  <PatientManagement />
                </RequiredAuth>
              }
            />
            <Route
              path={`${ROUTE_PATH.student.PATIENT}/:id`}
              element={
                <RequiredAuth>
                  <PatientDetail />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.student.SCORE_REPORT}
              element={
                <RequiredAuth>
                  <ScoreReport />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.student.PRACTICE_CHART}
              element={
                <RequiredAuth>
                  <PracticeChart />
                </RequiredAuth>
              }
            />
            <Route
              path={ROUTE_PATH.student.EVALUATION_CHART}
              element={
                <RequiredAuth>
                  <EvaluationChart />
                </RequiredAuth>
              }
            />
          </>
        )}

        <Route
          path={ROUTE_PATH.common.NOTICE}
          element={
            <RequiredAuth>
              <Notice />
            </RequiredAuth>
          }
        />
        <Route
          path={`${ROUTE_PATH.common.NOTICE_REGISTER}`}
          element={
            <RequiredAuth>
              <NoticeRegister />
            </RequiredAuth>
          }
        />
        <Route
          path={`${ROUTE_PATH.common.NOTICE}/:id`}
          element={
            <RequiredAuth>
              <NoticeDetail />
            </RequiredAuth>
          }
        />
        <Route
          path={ROUTE_PATH.common.MYPAGE}
          element={
            <RequiredAuth>
              <Mypage />
            </RequiredAuth>
          }
        />
        <Route
          path={ROUTE_PATH.common.MYPAGE_EDIT}
          element={
            <RequiredAuth>
              <Mypage />
            </RequiredAuth>
          }
        />
        <Route
          path={ROUTE_PATH.common.CHANGE_PASSWORD}
          element={
            <RequiredAuth>
              <ChangePassword />
            </RequiredAuth>
          }
        />

        {Object.entries(ROUTE_PATH.chart).map(([key, path]) => {
          const Component =
            chartComponents[key as keyof typeof chartComponents];
          const routes = ['practice', 'evaluation'].map((prefix) => {
            return [
              <Route
                key={`${prefix}-${key}`}
                path={`/${prefix}${path}/:id`}
                element={
                  <RequiredAuth>
                    {Component ? <Component /> : <div>Component Not Found</div>}
                  </RequiredAuth>
                }
              />,
              <Route
                key={`${prefix}-${key}`}
                path={`/${prefix}${path}/:id/register`}
                element={
                  <RequiredAuth>
                    {Component ? <Component /> : <div>Component Not Found</div>}
                  </RequiredAuth>
                }
              />,
            ];
          });
          return routes;
        })}

        {Object.entries(ROUTE_PATH.chart).map(([key, path]) => {
          const Component =
            chartComponents[key as keyof typeof chartComponents];
          const routes = ['feedback'].map((prefix) => {
            return [
              <Route
                key={`${prefix}-${key}`}
                path={`/${prefix}${path}/:id`}
                element={
                  <RequiredAuth>
                    {Component ? <Component /> : <div>Component Not Found</div>}
                  </RequiredAuth>
                }
              />,
            ];
          });
          return routes;
        })}

        <Route path={ROUTE_PATH.common.FORBIDDEN} element={<Forbidden />} />
        <Route path="*" element={<NotFound />}></Route>
      </Routes>

      {(modal.isOpen || modal.modalStack.length > 0) && (
        <Modal
          onClickOutside={
            modal?.onClickOutside ||
            modal?.modalStack?.[modal.modalStack.length - 1]?.onClickOutside
          }
        />
      )}
    </div>
  );
};

export default App;
